import { AlertTriangle, Link2, RefreshCw, Search, Send } from "lucide-react";
import { daysLeft } from "@/frontend/utils/shared";
import type { MagicLinkActivityRow, PatientSummary } from "@/server/schema/admin";
import type { NextStep } from "@/frontend/types/dashboard/next-step";

/** When a link runs out, phrased to sit inside a sentence. */
function expiryPhrase(expiresAt: string): string {
  const days = daysLeft(expiresAt);
  if (days <= 0) return "today";
  if (days === 1) return "tomorrow";
  return `in ${days} days`;
}

/**
 * Chooses one next step from the patient's state, most urgent first: a broken
 * connection outranks an unsent link, which outranks an unopened one. Returns
 * null once records are flowing and nothing needs a human. With `latestLink`
 * passed, an unopened link also reports when it runs out or that it already
 * has.
 *
 * In an individual workspace the reader IS the patient, so the link-shaped
 * states (`invited`, `opened`) can never be reached — nothing sends them a
 * magic link — and every step speaks to them directly instead of describing
 * somebody else's onboarding.
 *
 * Lives here rather than beside the card so it can be unit-tested as the pure
 * function it is; the card only decides how to paint what this returns.
 */
export function nextStepFor(
  summary: PatientSummary,
  options: { isIndividual?: boolean; latestLink?: MagicLinkActivityRow | null } = {},
): NextStep | null {
  const { row } = summary;
  const isIndividual = options.isIndividual ?? false;
  const latestLink = options.latestLink ?? null;

  if (summary.openIssueCount > 0) {
    const sourceWord = row.healthSystemCount === 1 ? "source" : "sources";
    return {
      icon: AlertTriangle,
      title: isIndividual
        ? "A health system needs you to sign in again"
        : "A health system needs a new sign-in",
      body: isIndividual
        ? `${summary.openIssueCount} of your ${row.healthSystemCount} connected ${sourceWord} stopped returning records. Signing in again restores the connection.`
        : `${summary.openIssueCount} of ${row.healthSystemCount} connected ${sourceWord} stopped returning records. Sending a fresh link lets them sign in again.`,
      actionLabel: isIndividual ? "Reconnect health systems" : "Review health systems",
      section: "onboarding",
      tone: "attention",
      showHealthSystemLogos: false,
    };
  }

  if (isIndividual) {
    if (row.healthSystemCount === 0) {
      return {
        icon: Link2,
        title: "Connect your first health system",
        body: "Search for the hospital, clinic or insurer you get care from and sign in with your patient portal details.",
        actionLabel: "Connect health systems",
        section: "onboarding",
        tone: "normal",
        showHealthSystemLogos: true,
      };
    }

    if (row.recordCount === 0) {
      return {
        icon: RefreshCw,
        title: "Connected, but no records yet",
        body: "A health system is linked and the first pull has not finished. Fetch records to start one now.",
        actionLabel: "View health systems",
        section: "onboarding",
        tone: "normal",
        showHealthSystemLogos: false,
      };
    }

    return null;
  }

  if (row.status === "added") {
    return {
      icon: Send,
      title: "Send this patient their link",
      body: "Generate a magic link and send it however you like. They sign in to their patient portals, and records start arriving here.",
      actionLabel: "Set up onboarding",
      section: "onboarding",
      tone: "normal",
      showHealthSystemLogos: row.healthSystemCount === 0,
    };
  }

  if (row.status === "invited") {
    if (latestLink?.expired) {
      return {
        icon: AlertTriangle,
        title: "Their link ran out before they opened it",
        body: "Access links last a few days. Generate a fresh one and send it again.",
        actionLabel: "Generate a new link",
        section: "onboarding",
        tone: "attention",
        showHealthSystemLogos: false,
      };
    }

    const runsOut = latestLink?.expiresAt ? ` It runs out ${expiryPhrase(latestLink.expiresAt)}.` : "";
    return {
      icon: Link2,
      title: "Their link is waiting to be opened",
      body: `The link is generated but has not been used yet.${runsOut} Resend it, or add suggested health systems so they see the right places first.`,
      actionLabel: "Open onboarding",
      section: "onboarding",
      tone: "normal",
      showHealthSystemLogos: row.healthSystemCount === 0,
    };
  }

  if (row.status === "opened" || row.healthSystemCount === 0) {
    return {
      icon: Search,
      title: "They opened the link but connected nothing",
      body: "Adding suggested health systems puts the right hospitals in front of them instead of asking them to search.",
      actionLabel: "Suggest health systems",
      section: "onboarding",
      tone: "normal",
      showHealthSystemLogos: row.healthSystemCount === 0,
    };
  }

  if (row.recordCount === 0) {
    return {
      icon: RefreshCw,
      title: "Connected, but no records yet",
      body: "A health system is linked and the first pull has not finished. Fetch records to start one now.",
      actionLabel: "View health systems",
      section: "onboarding",
      tone: "normal",
      showHealthSystemLogos: false,
    };
  }

  return null;
}
